/**
 * Shared copy and layout for “account pending approval” states (login modal + post-registration page).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { HourglassEmpty as HourglassIcon } from '@mui/icons-material';
import { FONT_SIZE } from '@/core/platform/platformStandards';

/**
 * @param {'manager'|'investor'|'generic'} audience
 * @param {'loginPending'|'registrationPending'} mode
 * @param {{ email?: string, userName?: string }} context
 */
export function getPendingApprovalCopy(audience, mode, context = {}) {
  const { email, userName } = context;
  const a =
    audience === 'investor' ? 'investor' : audience === 'manager' ? 'manager' : 'generic';

  if (mode === 'loginPending') {
    const title =
      a === 'investor' ? 'Account Under Review' : 'Application Under Review';
    const primary =
      a === 'investor'
        ? 'Your allocator account is being reviewed. You will receive an email from HFRConnect Admin (noreply@hfrconnect.ai) within 24–48 hours once the review is complete.'
        : 'Your account is in review. You will get an email from HFRConnect Admin (noreply@hfrconnect.ai) within 24–48 hours.';
    return {
      title,
      paragraphs: [primary],
      footerNote: 'Please make sure to check your spam email box.',
    };
  }

  const regTitle =
    a === 'investor' ? 'Registration Submitted' : 'Registration Submitted Successfully';

  const nameSuffix = userName ? `, ${userName}` : '';

  const p1 =
    a === 'investor'
      ? `Thank you for registering with HFRConnect${nameSuffix}. Your allocator profile is pending approval as part of our security and compliance requirements.`
      : `Thank you for registering with HFRConnect${nameSuffix}. Your account is currently pending approval as part of our security and compliance requirements.`;

  const p2 =
    'Our team will review your registration details within 24–48 hours. This process ensures the integrity and security of our platform for all users.';

  const p3 = email
    ? `Once approved, you will receive an email notification at ${email} with instructions to access your account. If you have any questions during this process, please contact our support team.`
    : 'Once approved, you will receive an email notification with instructions to access your account. If you have any questions during this process, please contact our support team.';

  return {
    title: regTitle,
    paragraphs: [p1, p2, p3],
    footerNote: null,
  };
}

const PendingApprovalPresentation = ({
  audience,
  mode,
  email,
  icon = 'hourglass',
  titleVariant = 'h5',
  userName,
  children,
}) => {
  const copy = getPendingApprovalCopy(audience, mode, { email, userName });

  return (
    <Box sx={{ textAlign: mode === 'registrationPending' ? 'center' : 'center' }}>
      {icon === 'hourglass' && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'info.light',
            mb: 2,
          }}
        >
          <HourglassIcon
            sx={{
              fontSize: FONT_SIZE.xxxl,
              color: 'info.main',
            }}
          />
        </Box>
      )}

      <Typography variant={titleVariant} sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
        {copy.title}
      </Typography>

      <Box
        sx={{
          textAlign: mode === 'registrationPending' ? 'left' : 'center',
          maxWidth: mode === 'registrationPending' ? { xs: '100%', md: 500 } : 'none',
          mx: mode === 'registrationPending' ? 'auto' : 0,
          mb: copy.footerNote ? 1 : 0,
        }}
      >
        {copy.paragraphs.map((text, i) => (
          <Typography
            key={i}
            variant="body1"
            paragraph
            sx={{ color: 'text.secondary', lineHeight: 1.6 }}
          >
            {text}
          </Typography>
        ))}
      </Box>

      {copy.footerNote && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: FONT_SIZE.md,
            fontStyle: 'italic',
            mt: 2,
          }}
        >
          {copy.footerNote}
        </Typography>
      )}

      {children}
    </Box>
  );
};

PendingApprovalPresentation.propTypes = {
  audience: PropTypes.oneOf(['manager', 'investor', 'generic']),
  mode: PropTypes.oneOf(['loginPending', 'registrationPending']).isRequired,
  email: PropTypes.string,
  icon: PropTypes.oneOf(['hourglass', 'none']),
  titleVariant: PropTypes.string,
  userName: PropTypes.string,
  children: PropTypes.node,
};

PendingApprovalPresentation.defaultProps = {
  audience: 'generic',
  icon: 'hourglass',
  titleVariant: 'h5',
  email: undefined,
  userName: undefined,
  children: null,
};

export default PendingApprovalPresentation;
